"use client";

/**
 * HeroLegibleAnimation
 *
 * Premium animated background for the Legible marketing hero.
 * Visually communicates the core product promise: messy HTML → clean Markdown → AI-readable.
 *
 * Layer architecture (back → front):
 *   1. Hash layer   — atmospheric floating # symbols
 *   2. Snippet layer — HTML→Markdown transformation cards (staggered)
 *   3. Scanner layer — abstract AI crawler sweep lines
 *
 * Design: dark navy, red for HTML clutter, blue for clean Markdown, cyan for scanners.
 * All animation is deterministic (no Math.random). Fully static-export safe.
 * Respects prefers-reduced-motion via Framer Motion's useReducedMotion().
 */

import { motion, useReducedMotion } from "framer-motion";

// ─── Timing constants ────────────────────────────────────────────────────────

/** Total seconds per snippet transformation cycle */
const CYCLE = 18;

/**
 * Keyframe timeline for each snippet (as fraction of CYCLE):
 *   0–30%   → HTML phase visible
 *   27–43%  → scan beam sweeps (transition window)
 *   40–96%  → Markdown phase visible
 *   96–100% → brief fade-out reset
 */
const HTML_TIMES = [0, 0.06, 0.30, 0.38, 1.0] as const;
const HTML_OPAC  = [0, 0.70, 0.70,  0,   0  ] as const;

const MD_TIMES   = [0, 0.38, 0.46, 0.94, 1.0] as const;
const MD_OPAC    = [0, 0,    0.75, 0.75,  0  ] as const;

// Scan beam: appears during the transition window, sweeps left→right
const BEAM_TIMES = [0, 0.26, 0.27, 0.42, 0.43, 1.0] as const;
const BEAM_OPAC  = [0, 0,    0.70, 0.70,  0,    0  ] as const;
const BEAM_X     = [-40, -40, 240,  240,  240,  240 ] as const; // pixels

// ─── Type definitions ────────────────────────────────────────────────────────

interface HashDef {
  x: number;       // left position (%)
  y: number;       // top position (%)
  size: string;    // Tailwind text-size class
  opacity: number; // base opacity (increase for drama)
  blur: number;    // px — higher = farther from viewer
  drift: number;   // vertical drift in px (negative = upward)
  duration: number;
  delay: number;
}

interface SnippetDef {
  x: number;       // left position (%)
  y: number;       // top position (%)
  delay: number;   // stagger delay (seconds)
  html: string[];  // before: messy HTML lines
  md: string[];    // after: clean Markdown lines
}

interface ScannerDef {
  x: number;       // left (%)
  y: number;       // top (%)
  width: number;   // width (%)
  opacity: number; // peak opacity (increase for drama)
  duration: number;
  delay: number;
  repeatDelay: number;
}

// ─── Layer data ───────────────────────────────────────────────────────────────

/**
 * Floating # symbols — atmospheric background texture.
 * Vary size, opacity, blur for perceived depth.
 * Production: keep opacity 0.08–0.22. For drama: raise to 0.35.
 */
const HASHES: HashDef[] = [
  { x:  4, y: 10, size: "text-sm",   opacity: 0.18, blur: 0, drift: -16, duration: 24, delay: 0    },
  { x: 14, y: 35, size: "text-xl",   opacity: 0.09, blur: 2, drift: -10, duration: 30, delay: 4.5  },
  { x: 22, y: 72, size: "text-xs",   opacity: 0.16, blur: 0, drift: -20, duration: 22, delay: 8    },
  { x: 36, y: 18, size: "text-2xl",  opacity: 0.07, blur: 3, drift: -8,  duration: 38, delay: 1.5  },
  { x: 42, y: 58, size: "text-sm",   opacity: 0.14, blur: 0, drift: -14, duration: 26, delay: 6    },
  { x: 50, y: 88, size: "text-lg",   opacity: 0.10, blur: 1, drift: -12, duration: 32, delay: 10   },
  { x: 58, y: 6,  size: "text-base", opacity: 0.20, blur: 0, drift: -18, duration: 20, delay: 2    },
  { x: 65, y: 45, size: "text-xl",   opacity: 0.08, blur: 2, drift: -10, duration: 28, delay: 7    },
  { x: 72, y: 80, size: "text-xs",   opacity: 0.15, blur: 0, drift: -22, duration: 23, delay: 12   },
  { x: 80, y: 22, size: "text-sm",   opacity: 0.18, blur: 0, drift: -14, duration: 21, delay: 3    },
  { x: 87, y: 55, size: "text-base", opacity: 0.11, blur: 1, drift: -8,  duration: 27, delay: 5    },
  { x: 93, y: 8,  size: "text-lg",   opacity: 0.13, blur: 2, drift: -16, duration: 33, delay: 0.5  },
  { x: 96, y: 40, size: "text-xs",   opacity: 0.17, blur: 0, drift: -12, duration: 19, delay: 9    },
  { x: 10, y: 85, size: "text-base", opacity: 0.09, blur: 3, drift: -6,  duration: 29, delay: 7.5  },
  { x: 48, y: 30, size: "text-sm",   opacity: 0.12, blur: 1, drift: -18, duration: 25, delay: 11   },
  { x: 30, y: 50, size: "text-xs",   opacity: 0.16, blur: 0, drift: -10, duration: 31, delay: 3.5  },
  { x: 76, y: 12, size: "text-2xl",  opacity: 0.06, blur: 4, drift: -8,  duration: 42, delay: 6.5  },
  { x: 20, y: 15, size: "text-sm",   opacity: 0.14, blur: 0, drift: -14, duration: 23, delay: 13   },
];

/**
 * Snippet transformation cards.
 * Each shows the same conceptual content transitioning from HTML clutter → Markdown.
 * Positioned in the right 50% of the viewport to avoid overlapping hero copy.
 * Staggered delays so they cycle independently.
 */
const SNIPPETS: SnippetDef[] = [
  {
    x: 54, y: 6, delay: 0,
    html: [
      '<div class="hero-wrap promo sticky">',
      '  <script src="analytics.min.js">',
      '  <div class="modal-backdrop z-50">',
      '  <span style="font-weight:700">CTA</span>',
    ],
    md: [
      "# Why AI Reads Structure",
      "## The content problem",
      "- Semantic markup matters",
      "**Insight:** Format = understanding",
    ],
  },
  {
    x: 72, y: 32, delay: 4,
    html: [
      '<nav class="nav-container main-nav">',
      '  <div class="sidebar-widget flex">',
      '  <script>window._track=true</script>',
      '  <link rel="preload" as="font">',
    ],
    md: [
      "## AI-Readable Navigation",
      "- Hierarchy over nesting",
      "- Headings carry meaning",
      "> Structure, not styles",
    ],
  },
  {
    x: 57, y: 62, delay: 8.5,
    html: [
      '<div class="container mx-auto flex">',
      '  <div class="col-md-8 sidebar">',
      '  <span class="badge badge-cta">',
      '  <footer class="sticky-footer">',
    ],
    md: [
      "### Structured Output",
      "- Paragraphs, not nested divs",
      "- Lists replace `<span>` stacks",
      "`markdown` is AI-native",
    ],
  },
  {
    x: 78, y: 68, delay: 2,
    html: [
      "<script src=\"bundle.min.js\"></script>",
      '<div class="cookie-banner z-999">',
      '<link rel="stylesheet" href="main.css">',
      '<meta property="og:image" content="">',
    ],
    md: [
      "## Token Efficiency",
      "**Before:** ~4,200 tokens/page",
      "**After:** ~340 tokens/page",
      "[Full article →](/) — AI-readable",
    ],
  },
];

/**
 * Abstract scanner sweep lines — represent AI crawler traversal.
 * Horizontal glowing lines that pass across the snippet area.
 * Production: opacity 0.15–0.25. For drama: raise to 0.45.
 */
const SCANNERS: ScannerDef[] = [
  { x: 48, y: 22, width: 36, opacity: 0.22, duration: 2.8, delay: 1.5, repeatDelay: 11 },
  { x: 52, y: 50, width: 30, opacity: 0.18, duration: 3.2, delay: 6.0, repeatDelay: 13 },
  { x: 55, y: 78, width: 34, opacity: 0.20, duration: 2.5, delay: 10.5, repeatDelay: 10 },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function HashLayer({ reduced }: { reduced: boolean }) {
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      {HASHES.map((h, i) => (
        <motion.span
          key={i}
          className={`absolute font-mono font-bold text-blue-300 select-none ${h.size}`}
          style={{
            left: `${h.x}%`,
            top: `${h.y}%`,
            opacity: h.opacity,
            filter: h.blur > 0 ? `blur(${h.blur}px)` : undefined,
          }}
          // Production: slow, subtle drift. For drama: increase yOffset and speeds.
          animate={reduced ? undefined : {
            y: [0, h.drift, 0],
            opacity: [h.opacity, h.opacity * 0.55, h.opacity],
          }}
          transition={reduced ? undefined : {
            duration: h.duration,
            delay: h.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          #
        </motion.span>
      ))}
    </div>
  );
}

/** Color-codes a single Markdown line based on its prefix syntax */
function MdLine({ line }: { line: string }) {
  let cls = "text-blue-300/80";
  let shadow: string | undefined;

  if (line.startsWith("# ") || line.startsWith("## ") || line.startsWith("### ")) {
    cls = "text-blue-200/90 font-semibold";
    shadow = "0 0 10px rgba(96,165,250,0.35)";
  } else if (line.startsWith(">")) {
    cls = "text-cyan-400/80 italic";
  } else if (line.startsWith("**") || line.includes("**")) {
    cls = "text-blue-100/85";
  } else if (line.startsWith("-")) {
    cls = "text-blue-400/80";
  } else if (line.startsWith("`") || line.includes("`")) {
    cls = "text-cyan-300/75";
  } else if (line.startsWith("[")) {
    cls = "text-blue-400/75 underline underline-offset-2";
  }

  return (
    <div
      className={`font-mono text-[10px] leading-[1.45] whitespace-nowrap ${cls}`}
      style={shadow ? { textShadow: shadow } : undefined}
    >
      {line}
    </div>
  );
}

/**
 * TransformSnippet
 * Renders one card that cycles:
 *   Phase A (HTML, red) → Phase B (scan beam) → Phase C (Markdown, blue) → reset
 * All three elements are stacked absolutely inside a fixed-size container.
 */
function TransformSnippet({ s, reduced }: { s: SnippetDef; reduced: boolean }) {
  const { x, y, delay, html, md } = s;

  const makeTransition = (times: readonly number[]) => ({
    duration: CYCLE,
    times: [...times],
    delay,
    repeat: Infinity,
    ease: "easeInOut" as const,
  });

  // In reduced motion: show snippets in their clean Markdown state, static
  if (reduced) {
    return (
      <div
        className="absolute pointer-events-none"
        style={{ left: `${x}%`, top: `${y}%`, width: 228 }}
      >
        <div className="bg-slate-950/50 border border-blue-500/20 rounded-md px-3 py-2 backdrop-blur-sm">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500/70" />
            <span className="font-mono text-[9px] text-blue-400/50 uppercase tracking-wider">markdown</span>
          </div>
          {md.map((line, i) => <MdLine key={i} line={line} />)}
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute pointer-events-none"
      style={{ left: `${x}%`, top: `${y}%`, width: 228 }}
    >
      {/* Fixed-height container stacks all phases */}
      <div className="relative h-24 overflow-hidden">

        {/* ── Phase A: HTML (red) ── */}
        <motion.div
          className="absolute inset-0 bg-slate-950/55 border border-red-500/20 rounded-md px-3 py-2 backdrop-blur-sm"
          animate={{ opacity: [...HTML_OPAC] }}
          transition={makeTransition(HTML_TIMES)}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500/70" />
            <span className="font-mono text-[9px] text-red-400/50 uppercase tracking-wider">raw html</span>
          </div>
          {html.map((line, i) => (
            <div key={i} className="font-mono text-[10px] leading-[1.45] whitespace-nowrap text-red-400/75">
              {line}
            </div>
          ))}
        </motion.div>

        {/* ── Phase B: Scan beam sweep ── */}
        {/* Opacity wrapper controls visibility window */}
        <motion.div
          className="absolute inset-0 pointer-events-none rounded-md overflow-hidden"
          animate={{ opacity: [...BEAM_OPAC] }}
          transition={makeTransition(BEAM_TIMES)}
        >
          {/* Inner element moves horizontally — clipped by parent overflow:hidden */}
          <motion.div
            className="absolute top-0 bottom-0 w-12"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(96,165,250,0.28) 40%, rgba(147,197,253,0.20) 60%, transparent)",
              boxShadow: "0 0 12px 2px rgba(96,165,250,0.12)",
            }}
            animate={{ x: [...BEAM_X] }}
            transition={makeTransition(BEAM_TIMES)}
          />
        </motion.div>

        {/* ── Phase C: Markdown (blue) ── */}
        <motion.div
          className="absolute inset-0 bg-slate-950/55 border border-blue-500/25 rounded-md px-3 py-2 backdrop-blur-sm"
          animate={{ opacity: [...MD_OPAC] }}
          transition={makeTransition(MD_TIMES)}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500/70" />
            <span className="font-mono text-[9px] text-blue-400/50 uppercase tracking-wider">markdown</span>
          </div>
          {md.map((line, i) => <MdLine key={i} line={line} />)}
        </motion.div>

      </div>
    </div>
  );
}

/**
 * ScannerLayer
 * Horizontal glowing lines that sweep across the viewport — implying AI indexing/parsing.
 * Not sci-fi: subtle, single-pixel lines with a soft glow gradient.
 */
function ScannerLayer({ reduced }: { reduced: boolean }) {
  if (reduced) return null;

  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      {SCANNERS.map((sc, i) => (
        <motion.div
          key={i}
          className="absolute h-px"
          style={{
            left: `${sc.x}%`,
            top: `${sc.y}%`,
            width: `${sc.width}%`,
            background:
              "linear-gradient(90deg, transparent, rgba(96,165,250,0.55) 30%, rgba(186,230,253,0.35) 60%, transparent)",
            boxShadow: "0 0 6px 1px rgba(96,165,250,0.15)",
          }}
          animate={{
            opacity: [0, sc.opacity, sc.opacity, 0],
            x: ["-15%", "0%", "0%", "115%"],
          }}
          transition={{
            duration: sc.duration,
            delay: sc.delay,
            times: [0, 0.08, 0.92, 1],
            repeat: Infinity,
            repeatDelay: sc.repeatDelay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

/**
 * HeroLegibleAnimation
 *
 * Drop this inside any hero `<section>` as an absolutely-positioned overlay.
 * Ensure the parent has `position: relative` and `overflow-hidden`.
 *
 * Usage:
 *   <section className="relative overflow-hidden ...">
 *     <HeroLegibleAnimation />
 *     <div className="relative z-10 ...">Your hero copy</div>
 *   </section>
 */
export function HeroLegibleAnimation() {
  const reduced = useReducedMotion() ?? false;

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none select-none overflow-hidden"
      // z-0 keeps this behind hero content (which uses z-10)
      style={{ zIndex: 0 }}
    >
      {/* Far layer: atmospheric # symbols */}
      <HashLayer reduced={reduced} />

      {/*
        Mid layer: snippet transformation cards.
        Desktop: all 4. Tablet (sm): first 3. Mobile: 1 card only.
        All positioned in the right 50%+ of the viewport.
      */}
      {SNIPPETS.map((s, i) => (
        <div
          key={i}
          className={
            i === 0
              ? "block"           // always show first snippet
              : i <= 2
              ? "hidden sm:block" // 2nd and 3rd: tablet+
              : "hidden lg:block" // 4th: desktop only
          }
        >
          <TransformSnippet s={s} reduced={reduced} />
        </div>
      ))}

      {/* Near layer: scanner sweep lines (desktop only for performance) */}
      <div className="hidden sm:block">
        <ScannerLayer reduced={reduced} />
      </div>
    </div>
  );
}

export default HeroLegibleAnimation;
